import { Typography } from '@breetometer/components';
import { capitalizeFirstLetter, type CURRENCYCODETYPE, CURRENCYCONFIG, type IApiResponse, type IVIPStats } from 'BreetHelpers';
import { useCurrencyOutput } from 'BreetHooks';
import { selectFiatMeta, useAppSelector } from 'BreetRedux';
import { type Dispatch, memo, type SetStateAction, useCallback } from 'react';

import { ANALYTICS_TABS, type AnalyticsTab } from './Analytics.helpers';

interface IAnalyticsStats {
	setActiveTab: Dispatch<SetStateAction<AnalyticsTab>>;
	activeTab: AnalyticsTab;
	vipStatsData?: IApiResponse<IVIPStats>;
}

export const AnalyticsStats = memo(({ vipStatsData, setActiveTab, activeTab }: IAnalyticsStats) => {
	const fiatMeta = useAppSelector(selectFiatMeta);

	const vipStats = vipStatsData?.data;
	const withdrawStats = vipStats?.withdrawalsAnalytics ?? [];
	const sellStats = vipStats?.sellsAnalytics ?? [];
	const conversionStats = vipStats?.conversionsAnalytics;

	const currencyOutput = useCurrencyOutput();
	const fiatValue = (code: CURRENCYCODETYPE, value?: number) => `${CURRENCYCONFIG[code].symbol}${currencyOutput(value ?? 0)}`;

	const currencyName = capitalizeFirstLetter(fiatMeta.currencyShort);
	const currency = fiatMeta.currency;

	const transactionRows = [
		{ label: 'Total Count', value: sellStats[0]?.totalCount ?? 0 },
		{ label: 'Total USD Amount', value: fiatValue('USD', sellStats[0]?.totalUsdAmount) },
		{ label: `Total ${currencyName} Amount`, value: fiatValue(currency, sellStats[0]?.totalNairaAmount) },
		{ label: 'Total Successful Trades', value: sellStats[0]?.successfulTrades ?? 0 },
		{ label: 'Total Pending Trades', value: sellStats[0]?.pendingTrades ?? 0 },
		{ label: 'Total Unsuccessful Trades', value: sellStats[0]?.unsucessfulTrades ?? 0 },
	];

	const withdrawalRows = [
		{ label: 'Total Naira Completed', value: fiatValue('NGN', withdrawStats[0]?.ngn.completedAmount) },
		{ label: 'Total Naira Processing', value: fiatValue('NGN', withdrawStats[0]?.ngn.processingAmount) },
		{ label: 'Total Naira Pending', value: fiatValue('NGN', withdrawStats[0]?.ngn.pendingAmount) },
		{ label: 'Total Naira Reversed', value: fiatValue('NGN', withdrawStats[0]?.ngn.reversedAmount) },
		{ label: 'Total Cedis Completed', value: fiatValue('GHS', withdrawStats[0]?.ghs.completedAmount) },
		{ label: 'Total Cedis Processing', value: fiatValue('GHS', withdrawStats[0]?.ghs.processingAmount) },
		{ label: 'Total Cedis Pending', value: fiatValue('GHS', withdrawStats[0]?.ghs.pendingAmount) },
		{ label: 'Total Cedis Reversed', value: fiatValue('GHS', withdrawStats[0]?.ghs.reversedAmount) },
		{ label: 'Total USD Completed', value: fiatValue('USD', withdrawStats[0]?.usd.completedAmount) },
		{ label: 'Total USD Processing', value: fiatValue('USD', withdrawStats[0]?.usd.processingAmount) },
		{ label: 'Total USD Pending', value: fiatValue('USD', withdrawStats[0]?.usd.pendingAmount) },
		{ label: 'Total USD Reversed', value: fiatValue('USD', withdrawStats[0]?.usd.reversedAmount) },
	];

	const sellsRows = [
		{ label: 'Total NGN', value: fiatValue('NGN', conversionStats?.ngn.totalAmount ?? 0) },
		{ label: 'Total NGN Count', value: conversionStats?.ngn.totalCount ?? 0 },
		{ label: 'Total GHS', value: fiatValue('GHS', conversionStats?.ghs.totalAmount ?? 0) },
		{ label: 'Total GHS Count', value: conversionStats?.ghs.totalCount ?? 0 },
		{ label: 'Total USD', value: fiatValue('USD', conversionStats?.usd.totalAmount ?? 0) },
		{ label: 'Total USD Count', value: conversionStats?.usd.totalCount ?? 0 },
	];

	const activeRowsObject: Record<AnalyticsTab, { label: string; value: number | string }[]> = {
		Transactions: transactionRows,
		Withdrawals: withdrawalRows,
		Conversions: sellsRows,
	};

	const activeRows = activeRowsObject[activeTab];

	const handleTabClick = useCallback(
		(tab: AnalyticsTab) => () => {
			setActiveTab(tab);
		},
		[setActiveTab]
	);

	return (
		<>
			<div className='appanalytics_subtabs'>
				{ANALYTICS_TABS.map((tab) => (
					<button
						key={tab}
						type='button'
						className={`appanalytics_subtab${activeTab === tab ? ' active bg-primary-400 border-primary-400 color-neutral-50' : ' color-primary-300'}`}
						onClick={handleTabClick(tab)}
					>
						{tab}
					</button>
				))}
			</div>

			<div className='appanalytics_statslist'>
				{activeRows.map(({ label, value }, index) => (
					<div
						key={label}
						className={`appanalytics_statsrow border-bottom-primary-400${index === activeRows.length - 1 ? ' last-child' : ''}`}
					>
						<Typography className='appanalytics_statsrow_label color-primary-100'>{label}</Typography>
						<Typography className='appanalytics_statsrow_value color-neutral-50'>{String(value)}</Typography>
					</div>
				))}
			</div>
		</>
	);
});

AnalyticsStats.displayName = 'AnalyticsStats';
